// Mode serveur : plan de salle en direct, ouverture de table, prise de
// commande rapide, reprise des tables QR, encaissement.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { staffFetch } from '../utils/staffApi';
import {
  StaffGate, useServiceSocket, useTicker, PayModal,
  euro, ago
} from './staffCommon';
import { ProductPicker, TabDetailPanel, sendOrderToTab } from './TabComponents';

function ServiceBoard({ staff, onLogout }) {
  const [tables, setTables] = useState(null);
  const [openTab, setOpenTab] = useState(null);      // détail d'addition affiché
  const [picker, setPicker] = useState(false);        // prise de commande en cours
  const [paying, setPaying] = useState(false);
  const [opening, setOpening] = useState(null);       // table libre en cours d'ouverture
  const [guestCount, setGuestCount] = useState('');
  useTicker(30000);

  const openTabRef = useRef(null);
  openTabRef.current = openTab;

  const load = useCallback(async () => {
    try {
      const res = await staffFetch('/service/overview', {}, onLogout);
      if (res.ok) {
        const body = await res.json();
        setTables(body.tables);
      }
    } catch {
      // retenté au prochain événement
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const connected = useServiceSocket({
    'tab-updated': (tab) => {
      load();
      if (openTabRef.current && tab && tab.id === openTabRef.current.id) {
        setOpenTab(tab);
      }
    },
    'new-order': () => load(),
    'order-updated': () => load()
  });

  const showTab = async (tabId) => {
    const res = await staffFetch(`/service/tab/${tabId}`, {}, onLogout);
    if (res.ok) setOpenTab(await res.json());
  };

  const openTable = async (table) => {
    const res = await staffFetch(`/service/tables/${table.id}/tab`, {
      method: 'POST',
      body: JSON.stringify(guestCount ? { guestCount: parseInt(guestCount) } : {})
    }, onLogout);
    if (res.ok) {
      const detail = await res.json();
      setOpening(null);
      setGuestCount('');
      setOpenTab(detail);
      setPicker(true); // enchaîner directement sur la prise de commande
      load();
    }
  };

  const sendOrder = async (items) => {
    const body = await sendOrderToTab(openTab.id, items);
    setOpenTab(body.tab);
    setPicker(false);
    load();
  };

  const cancelTab = async () => {
    const res = await staffFetch(`/service/tab/${openTab.id}/cancel`, { method: 'POST' }, onLogout);
    if (res.ok) {
      setOpenTab(null);
      load();
    }
  };

  return (
    <div className="staff-screen">
      <div className="staff-topbar">
        <h1><span className={`staff-conn ${connected ? 'on' : ''}`} /> Service</h1>
        <span className="staff-topbar-who">{staff && staff.name}</span>
        <button className="staff-logout" onClick={onLogout}>Quitter</button>
      </div>

      {tables === null && <div className="staff-loading" style={{ minHeight: '50vh' }}>Chargement…</div>}

      {tables && tables.length === 0 && (
        <div className="caisse-empty">
          Aucune table déclarée.<br />
          Le patron peut les créer dans Admin → Paramètres → Salle & QR codes.
        </div>
      )}

      <div className="svc-grid">
        {(tables || []).map(t => {
          const tab = t.tab;
          if (!tab) {
            return (
              <button key={t.id} className="svc-table" onClick={() => setOpening(t)}>
                <span className="svc-table-name">Table {t.name}</span>
                <span className="svc-table-free">Libre</span>
                <span className="svc-table-meta">Toucher pour ouvrir</span>
              </button>
            );
          }
          return (
            <button key={t.id} className="svc-table occupied" onClick={() => showTab(tab.id)}>
              <span className="svc-table-name">Table {t.name}</span>
              <span className="svc-table-total">{euro(tab.total)}</span>
              <span className="svc-table-meta">
                {tab.ordersCount} envoi{tab.ordersCount > 1 ? 's' : ''}
                {tab.lastOrderAt ? ` · ${ago(tab.lastOrderAt)}` : ''}
                {tab.guestCount ? ` · ${tab.guestCount} couv.` : ''}
              </span>
              <span className="svc-table-badges">
                {tab.openedBy === 'qr' && <span className="st-pill st-pending">QR</span>}
                {tab.inKitchen > 0 && <span className="st-pill st-preparing">⏳ {tab.inKitchen} en cuisine</span>}
                {tab.readyCount > 0 && <span className="st-pill st-ready">✅ {tab.readyCount} prêt{tab.readyCount > 1 ? 's' : ''}</span>}
              </span>
            </button>
          );
        })}
      </div>

      {/* Ouverture d'une table libre */}
      {opening && (
        <div className="staff-modal-overlay" onClick={() => setOpening(null)}>
          <div className="staff-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Ouvrir la table {opening.name}</h2>
            <input
              className="staff-input"
              type="number"
              inputMode="numeric"
              placeholder="Nombre de couverts (optionnel)"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
            />
            <div className="tabdetail-actions">
              <button className="staff-btn staff-btn-dark" onClick={() => setOpening(null)}>Annuler</button>
              <button className="staff-btn staff-btn-or" onClick={() => openTable(opening)}>
                Ouvrir & commander
              </button>
            </div>
          </div>
        </div>
      )}

      {openTab && !picker && (
        <TabDetailPanel
          tab={openTab}
          onClose={() => setOpenTab(null)}
          onAddOrder={() => setPicker(true)}
          onPay={() => setPaying(true)}
          onCancel={cancelTab}
        />
      )}

      {openTab && picker && (
        <ProductPicker
          title={`Table ${openTab.tableName}`}
          onClose={() => setPicker(false)}
          onSend={sendOrder}
        />
      )}

      {openTab && paying && (
        <PayModal
          tab={openTab}
          onClose={() => setPaying(false)}
          onPaid={(detail) => {
            setPaying(false);
            setOpenTab(null);
            load();
          }}
        />
      )}
    </div>
  );
}

export default function ServicePage() {
  return (
    <StaffGate screenName="Mode serveur">
      {(staff, logout) => <ServiceBoard staff={staff} onLogout={logout} />}
    </StaffGate>
  );
}
